/**
 * Soft≠Live Page135 / Page155 — External fleet / SEB desk
 * (Fleet mount + Response robots Page192 surface).
 * Operator copy is path-free. Seat posture drives catalogue vs write;
 * BE enforces tenant admin floor + PG engine on every mutate/list bus.
 */
import { useEffect, useMemo, useState } from "react";
import { html, F, Ico, KV, Card, Alert, Field, Input, Ticker, go, hashSearchParams } from "./ui.js";
import { path, useAction, useApi } from "./api.js";
import { envelopeSummary } from "./action-envelope.js";
import { useSession } from "./session.js";

/** Path-free operator copy for External / SEB failures. */
export function softExternalFleetErrorOf(err) {
  const n = String(err == null ? "" : err).trim();
  const low = n.toLowerCase();
  if (!low) return "That action could not be completed.";
  if (/\b403\b|forbidden|tenant_admin|not_authorized|denied/.test(low)) {
    return "Your account cannot change external fleet or SEB. Ask a manager if you need access.";
  }
  if (/\b401\b|session|unauthorized|missing bearer/.test(low)) {
    return "Sign in again to continue.";
  }
  if (/\b404\b|not found|disabled|external_robots/.test(low)) {
    return "That external fleet bus is not available.";
  }
  if (/\b422\b|\b409\b|invalid|required|conflict/.test(low)) {
    return "That change was not accepted.";
  }
  if (/\b429\b|too many/.test(low)) {
    return "Too many tries — wait a moment, then retry.";
  }
  if (/\b503\b|unavailable/.test(low)) {
    return "External fleet is not ready right now.";
  }
  if (/network|failed to fetch|timeout/.test(low)) {
    return "Network issue — try again in a moment.";
  }
  if (/\/api\/|\/external-robots|\/response\/robots|GET |POST |PUT |status_path|http/.test(low)) {
    return "That action could not be completed.";
  }
  if (n.length > 160) return n.slice(0, 157) + "…";
  return n;
}


const ASSIGN_KEY = "croc.ext.assign";
const ASSIGN_CONSUMED_KEY = "croc.ext.assign.consumed";
const ASSIGN_PIN_TTL_MS = 30 * 60 * 1000;

/** Soft≠Live: missing contract ops (flag-gated openapi) must not crash the desk. */
function safePath(operationId, options) {
  try {
    return path(operationId, options || {});
  } catch (e) {
    if (/Unknown operation_id/i.test(String((e && e.message) || e))) {
      if (operationId === "get__response_robots_status") return "/response/robots/status";
      if (operationId === "get__external-robots_status") return "/external-robots/status";
      return null;
    }
    throw e;
  }
}

/** Pure: seat chips from Live Response Robots posture (Page192). */
export function responseRobotsSeatOf(post) {
  const p = post && typeof post === "object" ? post : {};
  const lim = (p.limits && typeof p.limits === "object") ? p.limits : {};
  const iMax = Number(lim.items_paint_max);
  const nMax = Number(lim.intents_paint_max);
  const eMax = Number(lim.executions_paint_max);
  return {
    catalogueListAllowed: p.catalogue_list_allowed === true,
    intentCommandAllowed: p.intent_command_allowed === true,
    reReadAllowed: p.re_read_allowed === true,
    equipmentNavAllowed: p.equipment_nav_allowed === true,
    fleetNavAllowed: p.fleet_nav_allowed === true,
    productFlag: p.product_flag === true,
    engineArmed: p.engine_armed === true || p.armed === true,
    role: String(p.principal_role || ""),
    detail: p.detail || null,
    enabled: p.enabled === true,
    itemsPaintMax: Number.isFinite(iMax) && iMax > 0 ? Math.floor(iMax) : 40,
    intentsPaintMax: Number.isFinite(nMax) && nMax > 0 ? Math.floor(nMax) : 40,
    executionsPaintMax: Number.isFinite(eMax) && eMax > 0 ? Math.floor(eMax) : 40,
  };
}


const ROBOT_VENDORS = [
  { vendor: "dji", kind: "dji_cloud_api", label: "Air · DJI Cloud API" },
  { vendor: "unitree", kind: "unitree_edge_bridge", label: "Ground · Unitree bridge" },
];

function readAssignPin() {
  try {
    const raw = sessionStorage.getItem(ASSIGN_KEY);
    if (!raw) return null;
    const o = JSON.parse(raw);
    const lat = Number(o.lat);
    const lng = Number(o.lng);
    const at = Number(o.at) || 0;
    if (!Number.isFinite(lat) || !Number.isFinite(lng)) return null;
    if (at && Date.now() - at > ASSIGN_PIN_TTL_MS) {
      clearAssignPin();
      return null;
    }
    return {
      lat,
      lng,
      alt: Number.isFinite(Number(o.alt)) ? Number(o.alt) : null,
      at,
      handoff: o.handoff === true,
    };
  } catch (_) {
    return null;
  }
}

export function clearAssignPin() {
  try {
    sessionStorage.removeItem(ASSIGN_KEY);
  } catch (_) { /* ignore */ }
}

function markAssignPinConsumed(at) {
  try {
    sessionStorage.setItem(ASSIGN_CONSUMED_KEY, String(at || Date.now()));
  } catch (_) { /* ignore */ }
}

function assignPinAlreadyConsumed(at) {
  try {
    const prev = Number(sessionStorage.getItem(ASSIGN_CONSUMED_KEY) || 0);
    return Number.isFinite(at) && at > 0 && prev === at;
  } catch (_) {
    return false;
  }
}

export function writeAssignPin(lat, lng, alt, opts) {
  try {
    const handoff = !!(opts && opts.handoff);
    sessionStorage.setItem(
      ASSIGN_KEY,
      JSON.stringify({
        lat,
        lng,
        alt: alt == null ? null : alt,
        at: Date.now(),
        handoff,
      }),
    );
    if (handoff) sessionStorage.removeItem(ASSIGN_CONSUMED_KEY);
  } catch (_) { /* ignore */ }
}

/** Pure: flatten Live lists into KPI strip — never invent online Soft. */
export function fleetExternalKpisOf(pack) {
  const p = pack && typeof pack === "object" ? pack : {};
  const units = Array.isArray(p.units) ? p.units : [];
  const ints = Array.isArray(p.integrations) ? p.integrations : [];
  const bridges = Array.isArray(p.bridges) ? p.bridges : [];
  const intents = Array.isArray(p.intents) ? p.intents : [];
  const executions = Array.isArray(p.executions) ? p.executions : [];
  const online = bridges.filter((b) => b && b.online === true).length;
  const open = intents.filter((it) =>
    it && (it.status === "pending" || it.status === "claimed")).length;
  const withSeb = executions.filter((ex) => {
    if (!ex || !ex.receipt) return false;
    const r = ex.receipt.seb_receipt || ex.receipt;
    return !!(r && (r.evidence || r.sdk_code || r.ok != null));
  }).length;
  return {
    units: units.length,
    integrations: ints.length,
    bridgesOnline: online,
    bridgesTotal: bridges.length,
    openIntents: open,
    sebReceipts: withSeb,
  };
}

/** Pure: pack row items from GET envelope — blanks when missing (no Soft fill). */
export function fleetExternalItemsOf(pack, keys) {
  if (!pack || typeof pack !== "object") return [];
  for (const k of keys) {
    if (Array.isArray(pack[k])) return pack[k];
  }
  return [];
}

export function ExternalFleetDesk({ toast, surface = "fleet", initialVendor = "dji" }) {
  const isResponse = surface === "response";
  /* surface=resources reserved — Dispatch register mounts ResourcesRegisterDesk, not this desk. Soft≠Live. */
  const isResources = surface === "resources";
  const { me } = useSession();
  const role = String((me && me.role) || "").toLowerCase();
  const isUser = role === "user";
  const isSaStaff = role === "sa_staff";
  const { run, busy, error, envelope } = useAction();
  const [vendor, setVendor] = useState(initialVendor === "unitree" ? "unitree" : "dji");

  const posture = useApi(
    safePath(isResponse ? "get__response_robots_status" : "get__external-robots_status"),
  );
  const post = posture.data || null;
  /* Fail-closed while posture loads — never invent open catalogue seats. */
  const respSeat = responseRobotsSeatOf(isResponse ? (posture.loading ? null : post) : null);
  const armed = isResponse
    ? respSeat.catalogueListAllowed
    : (post && post.enabled === true);
  const intentOpen = isResponse
    ? respSeat.intentCommandAllowed
    : (post && post.write_allowed === true && post.enabled === true);
  const off = !armed;
  const itemsPaintMax = isResponse ? respSeat.itemsPaintMax : 40;
  const intentsPaintMax = isResponse ? respSeat.intentsPaintMax : 40;
  const executionsPaintMax = isResponse ? respSeat.executionsPaintMax : 40;

  const catalog = useApi(armed ? safePath("get__external-robots_catalog", { query: { vendor } }) : null);
  const commands = useApi(armed ? safePath("get__external-robots_commands", { query: { vendor } }) : null);
  const adapters = useApi(armed ? safePath("get__external-robots_bridge-adapters", { query: { vendor } }) : null);
  const integrations = useApi(armed ? safePath("get__external-robots_integrations") : null);
  const units = useApi(armed ? safePath("get__external-robots_units") : null);
  const bridges = useApi(armed ? safePath("get__external-robots_bridges") : null);
  const intents = useApi(
    (isResponse ? intentOpen : armed)
      ? safePath("get__external-robots_intents", { query: { limit: intentsPaintMax } })
      : null,
  );
  const executions = useApi(
    (isResponse ? intentOpen : armed)
      ? safePath("get__external-robots_executions", { query: { limit: executionsPaintMax } })
      : null,
  );
  const models = useApi(armed ? safePath("get__ai_models_mine_effective") : null);

  const [displayName, setDisplayName] = useState("");
  const [integrationId, setIntegrationId] = useState("");
  const [modelKey, setModelKey] = useState("");
  const [serial, setSerial] = useState("");
  const [unitId, setUnitId] = useState("");
  const [utterance, setUtterance] = useState("");
  const [lat, setLat] = useState("");
  const [lng, setLng] = useState("");
  const [alt, setAlt] = useState("");
  const [plan, setPlan] = useState(null);
  const [companionId, setCompanionId] = useState("");
  const [companionPurpose, setCompanionPurpose] = useState("field_companion");
  const [bridgeName, setBridgeName] = useState("");
  const [adapterKind, setAdapterKind] = useState("");
  const [bridgeConfig, setBridgeConfig] = useState({});

  useEffect(() => {
    const pin = readAssignPin();
    if (!pin) return;
    setLat(String(pin.lat));
    setLng(String(pin.lng));
    if (pin.alt != null) setAlt(String(pin.alt));
    const sp = hashSearchParams();
    const explicit = String(sp.get("from") || sp.get("handoff") || "").trim() === "map_assign"
      || pin.handoff === true;
    const once = !assignPinAlreadyConsumed(pin.at);
    if (explicit && once) {
      toast && toast("External destination loaded from map", "go");
      markAssignPinConsumed(pin.at);
    }
  }, []);

  const catModels = (catalog.data && Array.isArray(catalog.data.models))
    ? catalog.data.models.slice(0, itemsPaintMax)
    : [];
  const cmdItems = ((commands.data && commands.data.commands) || []).slice(0, itemsPaintMax);
  const adapterItems = (adapters.data && adapters.data.items) || [];
  const intItems = fleetExternalItemsOf(integrations.data, ["items", "integrations"]).slice(0, itemsPaintMax);
  const unitItems = fleetExternalItemsOf(units.data, ["items", "units"]).slice(0, itemsPaintMax);
  const bridgeItems = fleetExternalItemsOf(bridges.data, ["items", "bridges"]).slice(0, itemsPaintMax);
  const intentItems = fleetExternalItemsOf(intents.data, ["items", "intents"]).slice(0, intentsPaintMax);
  const executionItems = fleetExternalItemsOf(executions.data, ["items", "executions"]).slice(0, executionsPaintMax);
  const modelItems = useMemo(() => {
    const raw = models.data;
    if (!raw) return [];
    if (Array.isArray(raw.items)) return raw.items;
    if (Array.isArray(raw.models)) return raw.models;
    if (Array.isArray(raw)) return raw;
    return [];
  }, [models.data]);

  const kpis = useMemo(() => fleetExternalKpisOf({
    units: unitItems,
    integrations: intItems,
    bridges: bridgeItems,
    intents: intentItems,
    executions: executionItems,
  }), [unitItems, intItems, bridgeItems, intentItems, executionItems]);

  const selectedAdapter = adapterItems.find((a) => a.adapter_kind === adapterKind) || null;
  const configFields = (selectedAdapter && Array.isArray(selectedAdapter.config_fields))
    ? selectedAdapter.config_fields
    : [];

  useEffect(() => {
    if (!adapterItems.length) return;
    if (adapterKind && adapterItems.some((a) => a.adapter_kind === adapterKind)) return;
    setAdapterKind(String(adapterItems[0].adapter_kind || ""));
    setBridgeConfig({});
  }, [adapterItems, adapterKind]);

  const vendorMeta = ROBOT_VENDORS.find((v) => v.vendor === vendor) || ROBOT_VENDORS[0];
  const busErr = integrations.error || units.error || bridges.error || catalog.error;

  const addIntegration = async () => {
    if (isResponse && !intentOpen) {
      toast("Write closed for this seat", "warn");
      return;
    }
    if (!displayName) { toast("Name is required", "warn"); return; }
    const out = await run(path("post__external-robots_integrations"), {
      body: {
        vendor: vendorMeta.vendor,
        integration_kind: vendorMeta.kind,
        display_name: displayName,
      },
    });
    if (!out) { toast(softExternalFleetErrorOf(error || "Could not create integration"), "bad"); return; }
    integrations.reload();
    toast("Integration created — save the token now; it is shown once", "go");
    setDisplayName("");
  };

  const addUnit = async () => {
    if (isResponse && !intentOpen) {
      toast("Write closed for this seat", "warn");
      return;
    }
    if (!integrationId || !modelKey || !serial) {
      toast("Integration, model, and serial are required", "warn");
      return;
    }
    const out = await run(path("post__external-robots_units"), {
      body: {
        integration_id: integrationId,
        vendor: vendorMeta.vendor,
        model_key: modelKey,
        serial,
      },
    });
    if (!out) { toast(softExternalFleetErrorOf(error || "Could not register unit"), "bad"); return; }
    units.reload();
    toast("Unit registered", "go");
    setSerial("");
  };

  const saveBridge = async () => {
    if (isResponse && !intentOpen) {
      toast("Write closed for this seat", "warn");
      return;
    }
    if (!integrationId || !bridgeName || !adapterKind) {
      toast("Integration, bridge name, and adapter are required", "warn");
      return;
    }
    const out = await run(path("post__external-robots_bridges"), {
      body: {
        integration_id: integrationId,
        display_name: bridgeName,
        adapter_kind: adapterKind,
        config: bridgeConfig,
      },
    });
    if (!out) { toast(softExternalFleetErrorOf(error || "Could not save bridge"), "bad"); return; }
    bridges.reload();
    toast("SEB bridge registered — online only after edge heartbeat", "go");
  };

  const preview = async () => {
    if (isResponse && !intentOpen) {
      toast("Commands closed for this seat", "warn");
      return;
    }
    const out = await run(path("post__external-robots_intents_plan"), {
      body: {
        vendor: vendorMeta.vendor,
        utterance,
        lat: lat === "" ? null : Number(lat),
        lng: lng === "" ? null : Number(lng),
        alt: alt === "" ? null : Number(alt),
        source: lat !== "" && lng !== "" ? "map_assign" : "nl",
      },
    });
    if (!out) { toast(softExternalFleetErrorOf(error || "Could not plan"), "bad"); return; }
    setPlan(out);
    toast(out.executable ? "Plan ready" : "Plan needs coordinates", out.executable ? "go" : "warn");
  };

  const dispatch = async () => {
    if (isResponse && !intentOpen) {
      toast("Commands closed for this seat", "warn");
      return;
    }
    if (!unitId) { toast("Select a unit first", "warn"); return; }
    const out = await run(path("post__external-robots_intents"), {
      body: {
        unit_id: unitId,
        utterance,
        lat: lat === "" ? null : Number(lat),
        lng: lng === "" ? null : Number(lng),
        alt: alt === "" ? null : Number(alt),
        source: lat !== "" && lng !== "" && !utterance ? "map_assign" : "nl",
      },
    });
    if (!out) { toast(softExternalFleetErrorOf(error || "Could not dispatch"), "bad"); return; }
    intents.reload();
    toast(
      out.status === "pending"
        ? "Intent queued — waiting for edge acknowledgement"
        : "Intent stored",
      "go",
    );
  };

  const bindCompanion = async () => {
    if (isResponse && !intentOpen) {
      toast("Commands closed for this seat", "warn");
      return;
    }
    if (!unitId) { toast("Select a unit first", "warn"); return; }
    const out = await run(path("put__external-robots_units_p_companion", { pathParams: [unitId] }), {
      body: {
        companion_model_id: companionId || null,
        companion_purpose: companionPurpose || null,
      },
    });
    if (!out) { toast(softExternalFleetErrorOf(error || "Could not bind companion"), "bad"); return; }
    units.reload();
    toast("AI companion bound", "go");
  };

  const cancelIntent = async (intentId) => {
    if (isResponse && !intentOpen) {
      toast("Commands closed for this seat", "warn");
      return;
    }
    const out = await run(path("post__external-robots_intents_p_cancel", { pathParams: [intentId] }), { body: {} });
    if (!out) { toast(softExternalFleetErrorOf(error || "Could not cancel"), "bad"); return; }
    intents.reload();
    toast("Intent cancelled", "go");
  };

  const reRead = () => {
    posture.reload && posture.reload();
    catalog.reload && catalog.reload();
    commands.reload && commands.reload();
    adapters.reload && adapters.reload();
    integrations.reload && integrations.reload();
    units.reload && units.reload();
    bridges.reload && bridges.reload();
    intents.reload && intents.reload();
    executions.reload && executions.reload();
    models.reload && models.reload();
    toast && toast((isResponse ? "Response robots" : "External fleet") + " refreshed", "go");
  };

  const residual = isResponse ? "page192-robots" : "page155-external";
  const seatDetail = isResponse ? respSeat.detail : (post && post.detail);
  const writeOk = intentOpen;
  const productOn = isResponse ? respSeat.productFlag : !!(post && post.product_flag === true);
  const _engineOn = isResponse ? respSeat.engineArmed : !!(post && post.enabled === true);
  const postureLabel = (post && post.posture_label)
    || (off ? (isResponse ? "Robots sealed" : "External fleet sealed") : (isResponse ? "Robots open" : "External fleet open"));
  const postureLine = (post && post.posture_line) || "";
  const alertTitle = isResponse
    ? (off
      ? (seatDetail === "robots_admin_only"
        ? "Admin seat required"
        : seatDetail === "external_robots_disabled" || seatDetail === "external_robots_pg_required"
          ? "Response robots not armed"
          : "Response robots sealed")
      : "Response robots")
    : (off ? "External fleet not armed" : "Command and intent - SEB");
  const alertSay = isResponse
    ? (seatDetail === "robots_admin_only"
      ? "Catalogue and command buses are closed for this seat. Nothing is invented."
      : off
        ? (postureLine || "Response robots engine is not open for this seat.")
        : "Catalogue and commands open. Units move only after edge acknowledgement and receipt.")
    : (off
      ? (postureLine || "External robots engine is not armed — catalogue stays empty.")
      : "Intents and SEB leases are live. Progress marks are not success. Units move only after edge acknowledgement.");

  return html`<div className="fleet-ext-desk fleet-ext-desk--liquid"
    data-cmc-external="page40" data-cmc-page=${isResponse ? "page192-response-robots" : (isResources ? "resources-register" : "page135-external")}
    data-cmc-legacy="page75-external"
    data-resources-mode=${isResources ? "1" : "0"}
    data-cmc-residual=${residual}>
    <div className="fleet-ext-desk__rail">
      <div>
        <p className="eyebrow">${isResponse
          ? "Response · Robots / SEB"
          : isResources
            ? (vendor === "unitree" ? "Resources · Ground register" : "Resources · Air register")
            : "Fleet · External / SEB"}</p>
        <h2 className="fleet-ext-desk__title">${off
          ? (isResponse ? "Robots seat sealed" : (isResources ? "Register sealed" : "Buses · disarmed"))
          : (isResponse ? "Response robots" : (isResources ? "Connect · register · ready" : "Intent + SEB"))}</h2>
        <p className="note">
          ${postureLine || (isResponse
            ? "Catalogue and command buses for this seat."
            : isResources
              ? (off
                ? "Bridge not armed — empty register is honest. Mission send stays in Response."
                : "Register units here. Online only after edge heartbeat. Send stays in Response.")
              : (off
                ? "Engine not armed — empty catalogue is honest."
                : "Commands run only after edge acknowledgement and receipt."))}
        </p>
      </div>
      <div className="btn-row fleet-ext-desk__acts">
        <button type="button" className="btn btn--ghost btn--sm"
          disabled=${busy || posture.loading || (isResponse && !respSeat.reReadAllowed)}
          onClick=${reRead}>
          <${Ico} d="refresh" w=${14}/> Refresh
        </button>
        ${isResponse ? html`
          <button type="button" className="btn btn--ghost btn--sm"
            disabled=${!respSeat.equipmentNavAllowed}
            onClick=${() => go("/response/equipment")}>Equipment</button>
          <button type="button" className="btn btn--ghost btn--sm"
            disabled=${!respSeat.fleetNavAllowed}
            onClick=${() => go("/fleet/external")}>Fleet external</button>
        ` : isResources ? html`
          <button type="button" className="btn btn--ghost btn--sm"
            onClick=${() => go("/machine")}>Open Machine</button>
        ` : html`
          <button type="button" className="btn btn--ghost btn--sm"
            onClick=${() => go("/fleet/health")}>Health</button>
          <button type="button" className="btn btn--ghost btn--sm"
            onClick=${() => go("/fleet/diagnose")}>Why offline</button>
        `}
      </div>
    </div>

    <${Alert} tone=${off ? "warn" : "info"} title=${alertTitle} say=${alertSay}/>
    ${envelope ? html`<${KV} k="Last action" v=${envelopeSummary(envelope) || "—"} tone=${envelope.ok ? "hi" : "warn"}/>` : null}

    ${posture.error ? html`<${Alert} tone="warn" title="Could not load seat" say=${softExternalFleetErrorOf(posture.error)}/>` : null}
    <${Card} title="Your seat" meta=${postureLabel}>
      <p className="note">${postureLine || seatDetail || "—"}</p>
    <//>

    <div className="fleet-ext-desk__kpis" role="group" aria-label=${isResponse ? "Response robots posture KPIs" : "External posture KPIs"}>
      <div className="fleet-ext-desk__kpi" data-on=${armed ? "1" : "0"}>
        <span>${isResponse ? "Catalogue" : "Flag+PG"}</span>
        <b>${armed ? "open" : "sealed"}</b>
        <em>${isResponse ? "fleet bus" : "Live status"}</em>
      </div>
      <div className="fleet-ext-desk__kpi" data-on=${productOn ? "1" : "0"}>
        <span>Product flag</span>
        <b>${posture.loading ? "…" : (productOn ? "on" : "off")}</b>
        <em>EXTERNAL_ROBOTS_ENABLED</em>
      </div>
      <div className="fleet-ext-desk__kpi" data-on=${intentOpen ? "1" : "0"}>
        <span>${isResponse ? "Intent bus" : "Write seat"}</span>
        <b>${intentOpen ? "open" : "sealed"}</b>
        <em>${isResponse ? "independent" : "honesty"}</em>
      </div>
      <div className="fleet-ext-desk__kpi" data-on="0">
        <span>Your seat</span>
        <b>${isSaStaff ? "staff" : isUser ? "user" : (role || "—")}</b>
        <em>seat</em>
      </div>
    </div>

    ${busErr && !off ? html`
      <${Alert} tone="warn" title="Could not load external fleet"
        say=${softExternalFleetErrorOf(busErr)}/>` : null}

    <div className="fleet-ext-desk__kpis" role="group" aria-label="External fleet KPIs">
      ${(isResources
        ? [
            ["Units", kpis.units, kpis.units ? "1" : "0", "", "Live register"],
            ["Integrations", kpis.integrations, kpis.integrations ? "1" : "0", "", "Live"],
            ["Bridges online", kpis.bridgesOnline, kpis.bridgesOnline ? "1" : "0",
              kpis.bridgesTotal && !kpis.bridgesOnline ? "warn" : "",
              kpis.bridgesTotal ? (kpis.bridgesOnline + "/" + kpis.bridgesTotal + " hb") : "awaiting_edge_heartbeat"],
          ]
        : [
            ["Units", kpis.units, kpis.units ? "1" : "0", "", "Live register"],
            ["Integrations", kpis.integrations, kpis.integrations ? "1" : "0", "", "Live"],
            ["Bridges online", kpis.bridgesOnline, kpis.bridgesOnline ? "1" : "0",
              kpis.bridgesTotal && !kpis.bridgesOnline ? "warn" : "",
              kpis.bridgesTotal ? (kpis.bridgesOnline + "/" + kpis.bridgesTotal + " hb") : "awaiting_edge_heartbeat"],
            ["Open intents", kpis.openIntents, kpis.openIntents ? "1" : "0",
              kpis.openIntents ? "warn" : "", "pending · claimed"],
          ]
      ).map(([k, v, on, tone, em]) => html`
        <div key=${k} className="fleet-ext-desk__kpi" data-on=${on} data-tone=${tone}>
          <span>${k}</span>
          <b><${Ticker} value=${v}/></b>
          <em>${em}</em>
        </div>`)}
    </div>

    <div className="chips fleet-ext-desk__vendors">
      ${ROBOT_VENDORS.map((v) => html`
        <button key=${v.vendor} type="button" className="chip"
          data-tone=${vendor === v.vendor ? "go" : "ai"}
          data-sel=${vendor === v.vendor ? "1" : "0"}
          onClick=${() => setVendor(v.vendor)}>${v.label}</button>`)}
    </div>

    <div className="fleet-ext-desk__grid2">
      <${Card} title=${isResources ? "Model catalog" : "Catalog"} meta=${vendorMeta.label + " · catalog"}>
        ${catalog.error
          ? html`<${Alert} tone="warn" title="Catalog unavailable" say=${softExternalFleetErrorOf(catalog.error)}/>`
          : html`<div className="rows">
              ${!catModels.length
                ? html`<p className="note">No models yet.</p>`
                : catModels.slice(0, 32).map((c, i) => html`
                    <button key=${c.model_key || i} type="button" className="row"
                      onClick=${() => setModelKey(String(c.model_key || "").trim())}>
                      <span className="st"/>
                      <span className="main">
                        <b>${c.display_name || c.model_key || "—"}</b>
                        <span>${c.model_key || "model"}</span>
                      </span>
                    </button>`)}
            </div>`}
      <//>

      ${isResources ? null : html`<${Card} title="Command verbs" meta=${vendorMeta.label + " · verbs"}>
        ${commands.error
          ? html`<p className="note">${commands.error}</p>`
          : html`<div className="rows">
              ${!cmdItems.length
                ? html`<p className="note">No command catalog (flag off or vendor empty).</p>`
                : cmdItems.map((c) => html`
                    <div key=${c.verb} className="row row--say">
                      <${Ico} d="bolt" w=${14}/>
                      <span className="main">
                        <b>${c.display_name}</b>
                        <span>${c.verb} · ${c.canonical}${c.needs_coordinates ? " · needs lat/lng" : ""}</span>
                      </span>
                    </div>`)}
            </div>`}
      <//>`}
    </div>

    <${Card} title="Integrations & units" meta="create integration · register unit" cls="mt">
      <div className="fleet-ext-desk__grid2">
        <div>
          ${integrations.error
            ? html`<p className="note">${integrations.error}</p>`
            : html`<div className="rows">
                ${!intItems.length
                  ? html`<p className="note">Live empty — no integrations yet.</p>`
                  : intItems.slice(0, 20).map((x, i) => html`
                      <button key=${x.integration_id || i} type="button" className="row"
                        onClick=${() => setIntegrationId(String(x.integration_id || "").trim())}>
                        <span className="st"/>
                        <span className="main">
                          <b>${x.display_name || x.integration_id}</b>
                          <span>${x.vendor} · ${x.integration_kind}</span>
                        </span>
                      </button>`)}
              </div>`}
          <${Field} label="display_name">
            <${Input} icon="book" value=${displayName} onChange=${(e) => setDisplayName(e.target.value.trim())}/>
          <//>
          <button className="btn btn--sm btn--go" disabled=${busy || off || !writeOk} onClick=${addIntegration} style=${{ marginTop: 8 }}>
            Create integration</button>
        </div>
        <div>
          ${units.error
            ? html`<p className="note">${units.error}</p>`
            : html`<div className="rows">
                ${!unitItems.length
                  ? html`<p className="note">Live empty — no units registered.</p>`
                  : unitItems.slice(0, 24).map((u, i) => html`
                      <button key=${u.unit_id || i} type="button" className="row"
                        data-sel=${unitId === (u.unit_id || "") ? "1" : "0"}
                        onClick=${() => {
                          setUnitId(String(u.unit_id || "").trim());
                          setCompanionId(String(u.companion_model_id || "").trim());
                          if (u.integration_id) setIntegrationId(String(u.integration_id).trim());
                        }}>
                        <span className="st"/>
                        <span className="main">
                          <b>${u.serial || u.unit_id}</b>
                          <span>${u.vendor} · ${u.model_key}${u.companion_model_id ? " · AI" : ""}</span>
                        </span>
                      </button>`)}
              </div>`}
          <${Field} label="integration_id">
            <${Input} icon="book" value=${integrationId} onChange=${(e) => setIntegrationId(e.target.value.trim())}/>
          <//>
          <${Field} label="model_key">
            <${Input} icon="chip" value=${modelKey} onChange=${(e) => setModelKey(e.target.value.trim())}/>
          <//>
          <${Field} label="serial">
            <${Input} icon="bolt" value=${serial} onChange=${(e) => setSerial(e.target.value.trim())}/>
          <//>
          <button className="btn btn--sm btn--go" disabled=${busy || off || !writeOk} onClick=${addUnit} style=${{ marginTop: 8 }}>
            Register unit</button>
        </div>
      </div>
    <//>

    <${Card} title="Sentinel Edge Bridge (SEB)" meta="adapters · bridges · heartbeat = online" cls="mt">
      <p className="note">Dynamic adapter fields. Online only after Edge heartbeat (&lt;90s). Flight or walk verbs are never invented here.</p>
      <div className="chips" style=${{ marginBottom: 10 }}>
        ${adapterItems.map((a) => html`
          <button key=${a.adapter_kind} type="button" className="chip" data-tone="ai"
            data-sel=${adapterKind === a.adapter_kind ? "1" : "0"}
            onClick=${() => { setAdapterKind(a.adapter_kind); setBridgeConfig({}); }}>
            ${a.label || a.adapter_kind}
          </button>`)}
      </div>
      ${selectedAdapter && html`<p className="note">${selectedAdapter.notes || ""} · transport=${selectedAdapter.transport}</p>`}
      <${Field} label="bridge display_name">
        <${Input} icon="book" value=${bridgeName} onChange=${(e) => setBridgeName(e.target.value.trim())}/>
      <//>
      ${configFields.map((field) => html`
        <${Field} key=${field} label=${field}>
          <${Input} icon="chip" value=${String(bridgeConfig[field] || "")}
            onChange=${(e) => setBridgeConfig({ ...bridgeConfig, [field]: e.target.value.trim() })}/>
        <//>`)}
      <button className="btn btn--sm btn--go" disabled=${busy || off || !writeOk} onClick=${saveBridge} style=${{ marginTop: 8 }}>
        Save SEB bridge</button>
      <div className="rows" style=${{ marginTop: 12 }}>
        ${!bridgeItems.length
          ? html`<p className="note">${bridges.error || "No bridges yet — Edge cannot claim SEB envelopes until one is registered."}</p>`
          : bridgeItems.map((b) => html`
              <div key=${b.bridge_id} className="row row--say">
                <${Ico} d="bolt" w=${14}/>
                <span className="main">
                  <b>${b.display_name} · ${b.online === true
                    ? "online · heartbeat"
                    : (b.online === false
                      ? (b.status || "stale/offline · Live")
                      : (b.status || "— · no online flag assumed"))}</b>
                  <span>${b.adapter_kind} · ${b.bridge_id}
                    ${b.last_heartbeat_at ? ` · hb ${b.last_heartbeat_at}` : " · awaiting_edge_heartbeat"}</span>
                </span>
              </div>`)}
      </div>
    <//>

    ${isResources ? null : html`<${Card} title="NL dispatch · map coordinates" meta="plan · dispatch" cls="mt">
      <p className="note">Use an External destination saved from Ops map, or type lat/lng. Dispatch stays <b>pending</b> until edge ack+receipt (awaiting_edge_ack) — only after edge acknowledgement.</p>
      <${Field} label="unit_id">
        <${Input} icon="users" value=${unitId} onChange=${(e) => setUnitId(e.target.value.trim())}/>
      <//>
      <${Field} label="utterance" hint="e.g. fly to 1.30, 103.80 · return home · 巡逻">
        <${Input} icon="spark" value=${utterance} onChange=${(e) => setUtterance(e.target.value)}/>
      <//>
      <div className="fleet-ext-desk__grid3">
        <${Field} label="lat"><${Input} icon="target" value=${lat} onChange=${(e) => setLat(e.target.value.trim())}/><//>
        <${Field} label="lng"><${Input} icon="target" value=${lng} onChange=${(e) => setLng(e.target.value.trim())}/><//>
        <${Field} label="alt"><${Input} icon="bolt" value=${alt} onChange=${(e) => setAlt(e.target.value.trim())}/><//>
      </div>
      <div className="btn-row" style=${{ marginTop: 10 }}>
        <button className="btn btn--sm" disabled=${busy || off || !writeOk} onClick=${preview}>Preview plan</button>
        <button className="btn btn--sm btn--go" disabled=${busy || off || !writeOk} onClick=${dispatch}>Dispatch intent</button>
        <button type="button" className="btn btn--ghost btn--sm"
          disabled=${!lat && !lng}
          onClick=${() => {
            clearAssignPin();
            setLat(""); setLng(""); setAlt("");
            toast && toast("External destination cleared", "warn");
          }}>Clear destination</button>
      </div>
      ${plan && html`<p className="note" style=${{ marginTop: 10 }}>
        Plan: <b>${plan.verb}</b> (${plan.canonical}) · conf ${plan.confidence}
        ${plan.lat != null ? ` · ${plan.lat}, ${plan.lng}` : ""}
        · executable=${String(!!plan.executable)}
        ${plan.notes ? ` · ${plan.notes}` : ""}
      </p>`}
    <//>`}

    <${Card} title="AI companion sub-model" meta="bind model to unit" cls="mt">
      <p className="note">Bind a model from Settings → Model access. Empty clears. Spend rights are never invented.</p>
      <div className="rows" style=${{ maxHeight: 160, overflow: "auto", marginBottom: 8 }}>
        ${!modelItems.length
          ? html`<p className="note">${models.error || "No effective models (or catalogue off)."}</p>`
          : modelItems.slice(0, 24).map((m, i) => {
              const id = String(m.model_id || m.id || m.key || "").trim();
              if (!id) return null;
              return html`<button key=${id || i} type="button" className="row"
                onClick=${() => setCompanionId(id)}>
                <span className="st"/>
                <span className="main">
                  <b>${m.display_name || m.name || id}</b>
                  <span>${id}</span>
                </span>
              </button>`;
            })}
      </div>
      <${Field} label="companion_model_id">
        <${Input} icon="spark" value=${companionId} onChange=${(e) => setCompanionId(e.target.value.trim())}/>
      <//>
      <${Field} label="companion_purpose">
        <${Input} icon="book" value=${companionPurpose} onChange=${(e) => setCompanionPurpose(e.target.value.trim())}/>
      <//>
      <button className="btn btn--sm btn--go" disabled=${busy || off || !writeOk} onClick=${bindCompanion} style=${{ marginTop: 8 }}>
        Bind companion</button>
    <//>

    ${isResources ? null : html`<${Card} title="Execution ledger" meta="edge receipts only" cls="mt">
      <div className="btn-row" style=${{ marginBottom: 8 }}>
        <button className="btn btn--ghost btn--sm" type="button"
          onClick=${() => { executions.reload && executions.reload(); toast && toast("Executions refreshed", "go"); }}>
          Refresh executions</button>
        <span className="note" style=${{ alignSelf: "center" }}>
          SEB receipts Live · ${kpis.sebReceipts}/${executionItems.length}
        </span>
      </div>
      <p className="note">Claim→ack evidence trail. Flight is never assumed — only Edge seb_receipt outcomes. Progress marks ≠ success.</p>
      ${executions.error
        ? html`<p className="note">${executions.error}</p>`
        : html`<div className="rows">
            ${!executionItems.length
              ? html`<p className="note">Live empty — no executions yet. Edge worker must claim (edge worker claim only).</p>`
              : executionItems.map((ex) => {
                  const receipt = ex.receipt && (ex.receipt.seb_receipt || ex.receipt);
                  const hasSeb = !!(receipt && (receipt.evidence || receipt.sdk_code || receipt.ok != null));
                  return html`
                  <div key=${ex.execution_id} className="row row--say">
                    <${Ico} d="bolt" w=${14}/>
                    <span className="main">
                      <b>${ex.verb} · ${ex.status}${hasSeb ? " · SEB receipt" : " · no seb_receipt yet"}</b>
                      <span>${ex.execution_id} · ${ex.adapter_kind || "—"} · ${ex.execution || "—"}
                        ${ex.claim_expires_at ? ` · lease ${ex.claim_expires_at}` : ""}
                        ${Array.isArray(ex.progress) && ex.progress.length
                          ? ` · progress×${ex.progress.length} (≠success)`
                          : ""}
                        ${hasSeb
                          ? ` · evidence=${receipt.evidence || receipt.sdk_code || String(receipt.ok)}`
                          : ""}</span>
                    </span>
                  </div>`;
                })}
          </div>`}
    <//>

    <${Card} title="Intent queue" meta="pending · claimed · cancel" cls="mt">
      ${intents.error
        ? html`<p className="note">${intents.error}</p>`
        : html`<div className="rows">
            ${!intentItems.length
              ? html`<p className="note">Live empty — no intents yet.</p>`
              : intentItems.map((it) => html`
                  <div key=${it.intent_id} className="row row--say">
                    <${Ico} d="bolt" w=${14}/>
                    <span className="main">
                      <b>${it.verb} · ${it.status}</b>
                      <span>${it.unit_id} · ${it.execution || "—"}
                        ${it.lat != null ? ` · ${it.lat},${it.lng}` : ""}
                        ${it.utterance ? ` · “${it.utterance}”` : ""}</span>
                    </span>
                    ${(it.status === "pending" || it.status === "claimed") && html`
                      <button className="btn btn--sm" disabled=${busy}
                        onClick=${() => cancelIntent(it.intent_id)}>Cancel</button>`}
                  </div>`)}
          </div>`}
      ${error && html`<${Alert} tone="bad" title="Could not save" say=${softExternalFleetErrorOf(error)}/>`}
    <//>`}
  </div>`;
}

/** Response → Robots desk · Soft≠Live Page192 residual surface. */
export function RobotsDesk({ toast }) {
  return html`<${F} data-cmc-residual="page192-robots" data-cmc-page="page192-response-robots">
    <${ExternalFleetDesk} toast=${toast} surface="response"/>
  <//>`;
}
